import { Pipe, PipeTransform, inject } from '@angular/core';
import { L10nTranslationService } from 'angular-l10n';
import { isObject } from 'lodash-es';
import { Observable, map } from 'rxjs';

export interface LocalizableString {
  __unknown?: string;
  'es-ES'?: string;
  'en-GB'?: string;
  'en-US'?: string;
  'es-US'?: string;
  'de-DE'?: string;
  'it-IT'?: string;
  'fr-FR'?: string;
  'fr-CA'?: string;
}

@Pipe({ name: 'localize', standalone: true })
export class LocalizePipe implements PipeTransform {
  private translation = inject(L10nTranslationService);

  static localizeString(
    value: string | LocalizableString,
    languageCode: keyof LocalizableString
  ): string {
    return (isObject(value) ? value[languageCode] : value) ?? (value as string);
  }

  isValidJsonString(text: string): boolean {
    try {
      const o: unknown = JSON.parse(text);
      if (o && typeof o === 'object') {
        return true;
      }
    } catch (_e) {
      /* empty */
    }
    return false;
  }

  parseLocales(text: string | LocalizableString): LocalizableString | null {
    try {
      if (typeof text !== 'string') {
        return text;
      }
      const locale: unknown = JSON.parse(text);
      if (locale && typeof locale === 'object') {
        return locale as LocalizableString;
      }
    } catch (_e) {
      /* empty */
    }
    return null;
  }

  localize(value: LocalizableString, lang: string): string {
    const twoLang = lang.substring(0, 2);
    const existingLang = Object.keys(value).find((key) => key.startsWith(twoLang)) as
      | keyof LocalizableString
      | null;
    let translation: string | null = null;
    if (existingLang) {
      translation = value[existingLang] as string;
    }
    if (translation === null) {
      return (Object.values(value)[0] as string) || '';
    }
    return translation;
  }

  transform(value: string | null): Observable<string> {
    return this.translation.onChange().pipe(
      map(() => {
        const currentLang = this.translation.getLocale().language || 'en-GB';
        return this.transformToLocale(value, currentLang);
      })
    );
  }

  transformToLocale(value: string | null, currentLang: string): string {
    let translation = value;
    if (value && value !== '') {
      const locales = this.parseLocales(value);
      if (locales !== null) {
        translation = this.localize(locales, currentLang);
      }
      if (translation?.includes('{\\"')) {
        console.warn('Error in translation: ', value);
        return 'Error in translation';
      }
    }

    return translation ?? '';
  }

  transformLocale(value: string | undefined): string {
    if (!value) return '';
    let result = value;
    try {
      const locales = JSON.parse(value);
      let currentLang = this.translation.getLocale().language.replace('-', '_');
      if (currentLang) {
        currentLang = Object.keys(locales).find(
          (key) => key.startsWith(currentLang.substring(0, 2)) ?? currentLang
        ) as string;
      }

      if (locales[currentLang]) result = locales[currentLang];
    } catch (e) {
      console.warn(e);
    }
    return result;
  }
}
